/// <amd-module name="Repos/Views/PullRequest/Discussion/PullRequestDiscussionUIService" />
import { Comment, CommentThreadContext, CommentThreadStatus, CommentType, GitPullRequestCommentThread } from "Repos/Common/Contracts/Git";
import { DiscussionStatus, IDiscussionThreadModel, SpecialCommentPositions } from "Repos/Discussion/Common/DiscussionContracts";
import * as Resources from "Repos/Views/PullRequest/Resources";
import { showMessageDialog } from "VSS/Features/PlatformUI/MessageDialog";
import { IVssPageContext, IVssService, Services, VssService } from "VSS/Platform/Context";
import { IdentityRef } from "VSS/Platform/Contracts/WebApi";
import { getGitClient } from "../RestClient/Git";

export interface IPullRequestDiscussionContext {
    project: string;
    repositoryId: string;
    pullRequestId: number;
    thread: IDiscussionThreadModel;
}

/**
 * This server is not meant to be used directly.
 * Instead, PullRequestDiscussion component uses this services to talk
 * back to server to persist UI changes like adding a new comment,
 * updating a comment or changing the status of the discussion.
 */
export interface IPullRequestDiscussionUIService extends IVssService {
    addComment(
        discussionContext: IPullRequestDiscussionContext,
        commentId: number,
        comment: string,
        onAddThread?: (threadId: number, tempThreadId: number) => void,
        onAddComment?: (threadId: number) => void
    ): void;
    removeComment(
        discussionContext: IPullRequestDiscussionContext,
        commentId: number,
        onDeleteThread?: (threadId: number) => void,
        onDeleteComment?: (threadId: number) => void
    ): void;
    updateComment(
        discussionContext: IPullRequestDiscussionContext,
        commentId: number,
        comment: string,
        onUpdateComment?: (threadId: number) => void
    ): void;
    likeComment(discussionContext: IPullRequestDiscussionContext, commentId: number, like: boolean, identity: IdentityRef): void;
    changeStatus(
        discussionContext: IPullRequestDiscussionContext,
        status: DiscussionStatus,
        onChangeStatus?: (status: CommentThreadStatus) => void
    ): void;
    saveThread(discussionContext: IPullRequestDiscussionContext): Promise<void>;
}

class PullRequestDiscussionUIService extends VssService implements IPullRequestDiscussionUIService {
    public async addComment(
        discussionContext: IPullRequestDiscussionContext,
        commentId: number,
        comment: string,
        onAddThread?: (threadId: number, tempThreadId: number) => void,
        onAddComment?: (threadId: number) => void
    ): Promise<void> {
        const { project, pullRequestId, repositoryId, thread } = discussionContext;
        const threadId = thread.id;

        try {
            thread.startSaving(commentId);

            if (threadId > 0) {
                // Adding new comment to the existing thread
                const commentObj = {
                    commentType: CommentType.Text,
                    content: comment,
                    parentCommentId: thread.getParentCommentId()
                } as Comment;

                const newComment = await getGitClient(this.pageContext).createComment(commentObj, repositoryId, pullRequestId, threadId, project);
                thread.addComment(newComment);
            } else {
                // Starting a new thread
                const commentThread = {
                    ...thread.thread,
                    comments: [{ commentType: CommentType.Text, content: comment } as Comment],
                    status: CommentThreadStatus.Active
                } as GitPullRequestCommentThread;

                const { threadContext } = commentThread;

                // Trim line info if it doesn't point to an actual line
                if (threadContext && threadContext.filePath) {
                    let trimLineInfo = false;
                    if (
                        !threadContext.rightFileStart &&
                        !threadContext.rightFileEnd &&
                        threadContext.leftFileStart &&
                        threadContext.leftFileStart.line === SpecialCommentPositions.File &&
                        threadContext.leftFileEnd &&
                        threadContext.leftFileEnd.line === SpecialCommentPositions.File
                    ) {
                        trimLineInfo = true;
                    }

                    if (
                        !threadContext.leftFileStart &&
                        !threadContext.leftFileEnd &&
                        threadContext.rightFileStart &&
                        threadContext.rightFileStart.line === SpecialCommentPositions.File &&
                        threadContext.rightFileEnd &&
                        threadContext.rightFileEnd.line === SpecialCommentPositions.File
                    ) {
                        trimLineInfo = true;
                    }

                    if (trimLineInfo) {
                        commentThread.threadContext = { filePath: threadContext.filePath } as CommentThreadContext;
                    }
                }

                const newThread = await getGitClient(this.pageContext).createThread(commentThread, repositoryId, pullRequestId, project);
                thread.update(newThread);

                // Notify listeners about the new published thread
                onAddThread && onAddThread(newThread.id, threadId);
            }
            onAddComment && onAddComment(threadId);
            thread.finishSaving(commentId);
        } catch (error) {
            thread.setCommentError(commentId, error);
            thread.finishSaving(commentId);
        }
    }

    public async removeComment(
        discussionContext: IPullRequestDiscussionContext,
        commentId: number,
        onDeleteThread?: (threadId: number) => void,
        onDeleteComment?: (threadId: number) => void
    ): Promise<void> {
        const { project, pullRequestId, repositoryId, thread } = discussionContext;
        const threadId = thread.id;

        showMessageDialog(this.pageContext, {
            message: Resources.DeleteCommentMessage,
            okButtonProps: {
                text: Resources.DeleteButtonText
            },
            onClose: async result => {
                if (result) {
                    try {
                        await getGitClient(this.pageContext).deleteComment(repositoryId, pullRequestId, threadId, commentId, project);
                        thread.deleteComment(commentId);
                        onDeleteComment && onDeleteComment(thread.id);

                        if (!thread.hasComments()) {
                            onDeleteThread && onDeleteThread(thread.id);
                        }
                    } catch (error) {
                        thread.setError(error);
                    }
                }
            },
            title: Resources.DeleteCommentMessageTitle
        });
    }

    public async updateComment(
        discussionContext: IPullRequestDiscussionContext,
        commentId: number,
        comment: string,
        onUpdateComment?: (threadId: number) => void
    ): Promise<void> {
        const { project, pullRequestId, repositoryId, thread } = discussionContext;
        const threadId = thread.id;

        try {
            thread.startSaving(commentId);

            const commentObj = {
                commentType: CommentType.Text,
                content: comment,
                parentCommentId: thread.getParentCommentId()
            } as Comment;

            const updatedComment = await getGitClient(this.pageContext).updateComment(
                commentObj,
                repositoryId,
                pullRequestId,
                threadId,
                commentId,
                project
            );
            thread.updateComment(updatedComment);
            thread.finishSaving(commentId);

            onUpdateComment && onUpdateComment(threadId);
        } catch (error) {
            thread.setCommentError(commentId, error);
            thread.finishSaving(commentId);
        }
    }

    public async likeComment(
        discussionContext: IPullRequestDiscussionContext,
        commentId: number,
        like: boolean,
        identity: IdentityRef
    ): Promise<void> {
        const { project, pullRequestId, repositoryId, thread } = discussionContext;
        const threadId = thread.id;

        try {
            if (like) {
                await getGitClient(this.pageContext).createLike(repositoryId, pullRequestId, threadId, commentId, project);
            } else {
                await getGitClient(this.pageContext).deleteLike(repositoryId, pullRequestId, threadId, commentId, project);
            }

            thread.likeComment(commentId, like, identity);
        } catch (error) {
            thread.setError(error);
        }
    }

    public async changeStatus(
        discussionContext: IPullRequestDiscussionContext,
        status: DiscussionStatus,
        onChangeStatus?: (status: CommentThreadStatus) => void
    ): Promise<void> {
        const { project, pullRequestId, repositoryId, thread } = discussionContext;
        const threadId = thread.id;

        try {
            const patchThread = {
                status: status as number
            } as GitPullRequestCommentThread;

            const updatedThread = await getGitClient(this.pageContext).updateThread(patchThread, repositoryId, pullRequestId, threadId, project);
            thread.updateStatus(updatedThread.status as number);

            onChangeStatus && onChangeStatus(updatedThread.status);
        } catch (error) {
            thread.setError(error);
        }
    }

    public async saveThread(discussionContext: IPullRequestDiscussionContext): Promise<void> {
        const unsavedComments = discussionContext.thread.getComments().value.filter(c => c.isDirty().value);
        for (const comment of unsavedComments) {
            if (comment.isSaved()) {
                await this.updateComment(discussionContext, comment.getId(), comment.getContent().value);
            } else {
                await this.addComment(discussionContext, comment.getId(), comment.getContent().value);
            }
        }
    }
}

const serviceName = "IPullRequestDiscussionUIService";
Services.add(serviceName, { serviceFactory: PullRequestDiscussionUIService });

export function getPullRequestDiscussionUIService(pageContext: IVssPageContext): IPullRequestDiscussionUIService {
    return pageContext.getService<IPullRequestDiscussionUIService>(serviceName);
}
